import React from "react";
import { connect } from "react-redux";
import { navigate } from "gatsby";

import Helmet from "../components/Helmet";
import ConfirmDialog from "../components/Dialog/ConfirmDialog";
import Loader from "../components/Loader";
import Suggestion from "../components/Suggestions/Suggestion";
import isBrowser from "../utils/isBrowser";

const Feedback = ({ id, me, org }) => {
    const [ processing, setProcessing ] = React.useState(false);
    const [ success, setSuccess ] = React.useState(undefined);

    const [ feedback, setFeedback ] = React.useState({});
    const [ confirmMessage, setConfirmMessage ] = React.useState("");

    React.useEffect(() => {
        if (!org || !org._id) return;

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/feedback/" + id, {
            headers: {
                Accept: "application/json",
                Authorization: me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(feedback => {
            setSuccess(true);
            setFeedback(feedback);
        }).catch((e) => {
            console.error(e);
            setSuccess(false);
        });
    }, [ id, me.token, org ]);

    const updateStatus = (status) => {
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/feedback/" + id + "/status", {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                status,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(updatedFeedback => {
            setFeedback({
                ...feedback,
                ...updatedFeedback,
            });
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    const updateVote = (vote) => {
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/feedback/" + id + "/vote?user=true", {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                vote,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(updatedFeedback => {
            setFeedback({
                ...feedback,
                ...updatedFeedback,
            });
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    const deleteFeedback = () => {
        if (processing) return;

        setProcessing(true);
        setConfirmMessage("");

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/feedback/" + id, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(() => {
            setProcessing(false);
            navigate("/" + org.code + "/feedback");
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    return (
        <div css={{
            padding: 20,
            display: "flex",
            flexDirection: "column",
        }}>
            <Helmet
                title={ feedback.title + " - Feedback" }
                description={ feedback.details }
            />
            {
                success === true
                ?   <Suggestion
                        key={ feedback._id }
                        id={ feedback._id }
                        title={ feedback.title }
                        details={ feedback.details }
                        upvotes={ feedback.upvotes && feedback.upvotes instanceof Array && feedback.upvotes.length }
                        downvotes={ feedback.downvotes && feedback.downvotes instanceof Array && feedback.downvotes.length }
                        upvoted={ me && me.id && feedback.upvotes && feedback.upvotes instanceof Array && feedback.upvotes.includes(me.id.toString()) }
                        downvoted={ me && me.id && feedback.downvotes && feedback.downvotes instanceof Array && feedback.downvotes.includes(me.id.toString()) }
                        author={ feedback.user }
                        createdAt={ new Date(feedback.createdAt) }
                        closedAt={ feedback.closedAt ? new Date(feedback.closedAt) : null }
                        permissions={ feedback.permissions }
                        handleStatus={ updateStatus }
                        handleDelete={ () => setConfirmMessage("Are you sure you want to delete this feedback? This can't be undone.") }
                        handleVote={ updateVote }
                        authenticated={ me && me.token }
                    />
                :   success === false
                    ?   <div css={{ fontSize: ".8em", color: "darkgrey" }}>Oh, crap! Our servers having some issues. Please try again.</div>
                    :   <div css={{ textAlign: "center" }}><Loader /></div>
            }
            {
                confirmMessage
                ?   <ConfirmDialog
                        title="Delete Feedback"
                        message={ confirmMessage }
                        okHandler={ deleteFeedback }
                        cancelHandler={ () => setConfirmMessage("") }
                    />
                :   null
            }
        </div>
    );
};

export default connect(
    state => ({ me: state.global.me, org: state.global.org }),
    null,
)(Feedback);
